import { ApiBaseService, type ApiServiceConfig } from "./ApiBaseService";
import AppService from "./AppService";
import AuthService from "./AuthService";
import AutomationService from "./AutomationService";
import ThirdPartyService from "~/services/ThirdPartyService";
import RunService from "~/services/RunService";
import EventService from "~/services/EventService";
import ConnectionService from "~/services/ConnectionService";
import TaskService from "~/services/TaskService";
import LiveStatService from "~/services/LiveStatService";
import PeopleService from "~/services/PeopleService";
import LocalizationService from "~/services/LocalizationService";
import ExternalObjectService from "~/services/ExternalObjectService";
import UserService from "~/services/UserService";
import AccountService from "~/services/AccountService";
import DomainService from "~/services/DomainService";
import MailTemplateService from "~/services/MailTemplateService";
import BrandService from "~/services/BrandService";
import GoogleService from "~/services/GoogleService";
import ChatService from "~/services/ChatService";
import TemplateService from "~/services/TemplateService";
import LocationService from "~/services/LocationService";
import FormService from "~/services/FormService";
import DataSourceService from "~/services/DataSourceService";
import type { ApiResponse } from "~/types/ApiResponse";
import type { FieldDefinition } from "~/models/Automation";
import CoreSignalService from "./CoreSignalService";
import MessageService from "~/services/MessageService";
import ResponseService from "~/services/ResponseService";
import { SafetyLimitsService } from "~/services/SafetyLimitsService";
import { PartnerApiContainer } from "~/services/partners/PartnerApiContainer";
import BrandFetchService from "./BrandFetchService";
import { AiToolkitService } from "./AiToolkitService";
import CreditService from "./CreditService";
import type { FilterGroup } from "~/components/automation/step/FilterDisplay.vue";
export type EnumListType = "apps" | "core-apps";
export type EnumItem<T = {}> = T & {
    label: string;
    value: string;
};

export type LoomVideo = {
    id: string;
    app: string;
    title: string;
    description: string;
    html: string;
    thumbnail_url: string;
    share_url: string;
    created_at: string;
    updated_at: string;
};

export class ApiServiceContainer extends ApiBaseService {
    public apps: AppService;
    public users: UserService;
    public connections: ConnectionService;
    public external_objects: ExternalObjectService;
    public auth: AuthService;
    public automations: AutomationService;
    public third_party: ThirdPartyService;
    public events: EventService;
    public processes: RunService;
    public tasks: TaskService;
    public live_stats: LiveStatService;
    public people: PeopleService;
    public localization: LocalizationService;
    public account: AccountService;
    public domains: DomainService;
    public mail_templates: MailTemplateService;
    public brands: BrandService;
    public google: GoogleService;
    public locations: LocationService;
    public core_signal: CoreSignalService;
    public chat: ChatService;
    public templates: TemplateService;
    public forms: FormService;
    public data_sources: DataSourceService;
    public messages: MessageService;
    public responses: ResponseService;
    public safety_limits: SafetyLimitsService;
    public partners: PartnerApiContainer;
    public brandFetch: BrandFetchService;
    public ai_toolkit: AiToolkitService;
    public credits: CreditService;

    constructor(config: ApiServiceConfig) {
        super(config);

        this.apps = new AppService(config);
        this.users = new UserService(config);
        this.connections = new ConnectionService(config);
        this.external_objects = new ExternalObjectService(config);
        this.auth = new AuthService(config);
        this.automations = new AutomationService(config);
        this.events = new EventService(config);
        this.processes = new RunService(config);
        this.tasks = new TaskService(config);
        this.third_party = new ThirdPartyService(config);
        this.live_stats = new LiveStatService(config);
        this.people = new PeopleService(config);
        this.localization = new LocalizationService(config);
        this.account = new AccountService(config);
        this.domains = new DomainService(config);
        this.mail_templates = new MailTemplateService(config);
        this.brands = new BrandService(config);
        this.google = new GoogleService(config);
        this.locations = new LocationService(config);
        this.core_signal = new CoreSignalService(config);
        this.chat = new ChatService(config);
        this.templates = new TemplateService(config);
        this.forms = new FormService(config);
        this.data_sources = new DataSourceService(config);
        this.messages = new MessageService(config);
        this.responses = new ResponseService(config);
        this.safety_limits = new SafetyLimitsService(config);
        this.partners = new PartnerApiContainer(config);
        this.brandFetch = new BrandFetchService(config);
        this.ai_toolkit = new AiToolkitService(config);
        this.credits = new CreditService(config);
    }

    public getFieldCollection(
        type:
            | "brand"
            | "location"
            | "mail-template"
            | "form"
            | "connection"
            | "custom-app",
        id_or_uuid: string | number = "default",
        config: Record<string, any> = {},
    ): Promise<ApiResponse<FieldDefinition[]>> {
        return this.request<ApiResponse<FieldDefinition[]>>(
            this.route(
                "api.field_maps.type",
                "field_maps/{type}/{id_or_uuid}",
                {
                    type,
                    id_or_uuid,
                },
            ),
            {
                method: "POST",
                body: config,
            },
        );
    }

    public getObjectFieldCollection(
        type: "connection" | "custom-app",
        id_or_uuid: string | number = "default",
        object: string,
        config: Record<string, any> = {},
    ): Promise<ApiResponse<FieldDefinition[]>> {
        return this.request<ApiResponse<FieldDefinition[]>>(
            this.route(
                "api.field_maps.object",
                "field_maps/{type}/{id_or_uuid}/{object}",
                {
                    type,
                    id_or_uuid,
                    object,
                },
            ),
            {
                method: "POST",
                body: config,
            },
        );
    }

    public getValueCollection(
        type: "brand" | "location" | "automation",
        id_or_uuid: string | number = "default",
        config: Record<string, any> = {},
    ): Promise<ApiResponse<Record<string, any>>> {
        return this.request<ApiResponse<Record<string, any>>>(
            this.route(
                "api.value_maps.type",
                "value_maps/{type}/{id_or_uuid}",
                {
                    type,
                    id_or_uuid,
                },
            ),
            {
                method: "POST",
                body: config,
            },
        );
    }

    public getEnumList<T = {}>(
        type: EnumListType,
    ): Promise<ApiResponse<EnumItem<T>[]>> {
        return this.request<ApiResponse<EnumItem<T>[]>>(
            this.route("api.enums.index", "enums/{type}", {
                type,
            }),
        );
    }

    public getResourceList(
        type:
            | "users"
            | "brands"
            | "locations"
            | "forms"
            | "automations"
            | "apps"
            | "emails"
            | "all_users",
        config: {
            include_id_in_label?: boolean;
        } & Record<string, any> = {},
    ): Promise<ApiResponse<{ label: string; value: string; metadata: any }[]>> {
        return this.request<
            ApiResponse<{ label: string; value: string; metadata: any }[]>
        >(
            this.route("api.lists.index", "lists/{type}", {
                type,
            }),
            {
                method: "GET",
                query: config,
            },
        );
    }

    public searchLoomVideos(
        query?: string,
        apps?: string[],
    ): Promise<ApiResponse<LoomVideo[]>> {
        return this.request<ApiResponse<LoomVideo[]>>(
            this.route("api.help.videos", "help/videos"),
            {
                method: "POST",
                body: {
                    query,
                    apps,
                },
            },
        );
    }
}
